import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart, Line, ScatterChart, Scatter, ComposedChart, Bar,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { apiUrl, type UnitScale, unitLabel } from '../App';
import { authedFetch } from '../supabaseClient';
import KpiTooltip from '../KpiTooltip';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CacTableData {
  sm: Record<string, number>;
  new_customers: Record<string, number>;
  monthly_cac: Record<string, number | null>;
  quarterly_cac: Record<string, number | null>;
  ttm_cac: Record<string, number | null>;
}

interface PaybackData {
  cohort_list: string[];
  cohort_axis?: string[];  // canonical customer-period axis (incl. empty cohorts)
  acquired: Record<string, number>;
  adaptive_cac: Record<string, number | null>;
  cac_window: Record<string, string>;
  gm_over_cac: (number | null)[][];
  max_offsets: number;
}

interface CacResponse {
  period_cols: string[];
  period_type: string;
  cac_table: CacTableData;
  current_quarterly_cac: number | null;
  current_gm_pct: number | null;
  revenue_to_payback: number | null;
  cac_vs_spend: { cumulative_sm: number; cac: number }[];
  payback: PaybackData;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriod(period: string): string {
  const [year, month] = period.split('-');
  return `${parseInt(month)}/${year}`;
}

let _scale = 1000;

function formatVal(val: number): string {
  if (val === 0) return '';
  const abs = Math.abs(val) / _scale;
  const str = abs < 1 ? abs.toFixed(1) : Math.round(abs).toLocaleString();
  return val < 0 ? `(${str})` : str;
}

function formatValOrNA(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return formatVal(val) || '0';
}

function formatCount(val: number | null | undefined): string {
  if (val === null || val === undefined || val === 0) return '';
  return val.toLocaleString();
}

function formatPctInt(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return Math.round(val) + '%';
}

function formatRatio(val: number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return val.toFixed(1) + 'x';
}

function cohortColors(count: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const t = count <= 1 ? 0.5 : i / (count - 1);
    const h = 210 + t * 30;
    const s = 60 - t * 20;
    const l = 35 + t * 35;
    colors.push(`hsl(${h}, ${s}%, ${l}%)`);
  }
  return colors;
}

// ── CAC Table ────────────────────────────────────────────────────────────────

function CacTable({ data, projectId }: { data: CacResponse; projectId: string }) {
  const { period_cols, period_type, cac_table } = data;
  const cacLabel = period_type === 'quarterly' ? 'Quarterly CAC' : 'Monthly CAC';

  type DataRow = { label: string; render: (p: string) => string; kpiKey?: string };
  const rows: DataRow[] = [
    { label: 'S&M',                   render: p => formatVal(cac_table.sm[p] ?? 0) },
    { label: '# of New Customers',    render: p => formatCount(cac_table.new_customers[p]) },
    { label: cacLabel,                render: p => formatValOrNA(cac_table.monthly_cac[p]), kpiKey: 'monthly_cac' },
    { label: 'Trailing 3-Period CAC', render: p => formatValOrNA(cac_table.quarterly_cac[p]), kpiKey: 'trailing_3_period_cac' },
    { label: 'TTM CAC',               render: p => formatValOrNA(cac_table.ttm_cac[p]), kpiKey: 'ttm_cac' },
  ];

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg">
      <div className="overflow-auto max-h-[40vh]">
        <table className="w-full text-[11px] md:text-xs">
          <thead className="sticky top-0 z-20">
            <tr className="border-b border-gray-700 bg-gray-900">
              <th className="sticky left-0 z-30 bg-gray-900 text-left px-3 md:px-5 py-2.5 w-28 md:w-52 text-gray-500 text-[11px] md:text-xs font-medium"></th>
              {period_cols.map(p => (
                <th key={p} className="bg-gray-900 text-right px-3 md:px-6 py-2.5 font-bold text-gray-200 text-[11px] md:text-sm whitespace-nowrap min-w-[72px]">
                  {formatPeriod(p)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.label} className="border-b border-gray-800/60 hover:bg-gray-800/30">
                <td className="sticky left-0 z-10 bg-gray-900 px-3 md:px-5 py-2 text-gray-300 text-[11px] md:text-xs md:whitespace-nowrap font-medium">{row.label}</td>
                {period_cols.map(p => (
                  <td key={p} className="text-right px-3 md:px-6 py-2 text-gray-300 text-[11px] md:text-xs whitespace-nowrap">
                    {row.kpiKey
                      ? <KpiTooltip kpiKey={row.kpiKey} projectId={projectId}>{row.render(p)}</KpiTooltip>
                      : row.render(p)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── CAC Callout Box ──────────────────────────────────────────────────────────

function CacCallout({ data, projectId }: { data: CacResponse; projectId: string }) {
  const cac = data.current_quarterly_cac;
  const gm = data.current_gm_pct;
  const rev = data.revenue_to_payback;

  return (
    <div className="bg-gray-800/50 border border-gray-800 rounded-lg px-3 md:px-5 py-4 max-w-xs">
      <h4 className="text-sm font-bold text-white mb-3">Payback Summary</h4>
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-300">Current Quarterly CAC</span>
          <span className="text-white font-medium">{cac != null ? formatVal(cac) : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-300">Gross Margin</span>
          <span className="text-white font-medium">{gm != null ? `${gm}%` : '—'}</span>
        </div>
        <div className="border-t border-gray-700 pt-1.5 flex justify-between">
          <span className="text-gray-400 font-medium">Revenue to Payback</span>
          <span className="text-white font-bold">
            <KpiTooltip kpiKey="revenue_to_payback" projectId={projectId}>{rev != null ? formatVal(rev) : '—'}</KpiTooltip>
          </span>
        </div>
      </div>
    </div>
  );
}

// ── CAC Charts ───────────────────────────────────────────────────────────────

function CacLineChart({ data }: { data: CacResponse }) {
  const chartData = useMemo(() =>
    data.period_cols.map(p => ({
      name: formatPeriod(p),
      cac: data.cac_table.monthly_cac[p] != null ? data.cac_table.monthly_cac[p]! / _scale : null,
      ttm: data.cac_table.ttm_cac[p] != null ? data.cac_table.ttm_cac[p]! / _scale : null,
    })),
    [data, _scale]
  );

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">CAC & TTM CAC</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tickFormatter={(v: number) => `$${Math.round(v).toLocaleString()}`} tick={{ fill: '#6b7280', fontSize: 10 }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            formatter={(value: any, name: any) => [`$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`, name]}
          />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Line type="monotone" dataKey="cac" name="CAC" stroke="#3b82f6" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
          <Line type="monotone" dataKey="ttm" name="TTM CAC" stroke="#22c55e" strokeWidth={2} dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CacScatterChart({ data }: { data: CacResponse }) {
  const chartData = useMemo(() =>
    data.cac_vs_spend.map(d => ({
      x: d.cumulative_sm / _scale,
      y: d.cac / _scale,
    })),
    [data, _scale]
  );

  // Simple linear trendline
  const trendData = useMemo(() => {
    if (chartData.length < 2) return [];
    const n = chartData.length;
    const sumX = chartData.reduce((s, d) => s + d.x, 0);
    const sumY = chartData.reduce((s, d) => s + d.y, 0);
    const sumXY = chartData.reduce((s, d) => s + d.x * d.y, 0);
    const sumXX = chartData.reduce((s, d) => s + d.x * d.x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const xMin = Math.min(...chartData.map(d => d.x));
    const xMax = Math.max(...chartData.map(d => d.x));
    return [
      { x: xMin, y: intercept + slope * xMin },
      { x: xMax, y: intercept + slope * xMax },
    ];
  }, [chartData]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">CAC as Spend Increases</h3>
      <ResponsiveContainer width="100%" height={300}>
        <ScatterChart>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis type="number" dataKey="x" name="Cumulative S&M" tickFormatter={(v: number) => `$${Math.round(v).toLocaleString()}`} tick={{ fill: '#6b7280', fontSize: 10 }} />
          <YAxis type="number" dataKey="y" name="CAC" tickFormatter={(v: number) => `$${Math.round(v).toLocaleString()}`} tick={{ fill: '#6b7280', fontSize: 10 }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            formatter={(value: any, name: any) => [`$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`, String(name) === 'Cumulative S&M' ? 'S&M' : 'CAC']}
          />
          <Scatter data={chartData} fill="#3b82f6" fillOpacity={0.7} r={4} />
          {trendData.length === 2 && (
            <Scatter data={trendData} fill="none" line={{ stroke: '#ef4444', strokeWidth: 1.5, strokeDasharray: '4 4' }} r={0} />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Payback Matrix ───────────────────────────────────────────────────────────

function PaybackMatrix({ payback, projectId, periodType }: { payback: PaybackData; projectId: string; periodType: string }) {
  const { cohort_list, cohort_axis, acquired, adaptive_cac, cac_window, gm_over_cac, max_offsets } = payback;
  const [collapsed, setCollapsed] = useState(false);

  // Canonical cohort axis: every model period gets a labelled row (empty
  // cohorts blank), matching the export and Cohort Analysis. Falls back to
  // the observed list if the backend hasn't sent the axis yet.
  const axis = cohort_axis && cohort_axis.length > 0 ? cohort_axis : cohort_list;
  const observedPos = useMemo(
    () => new Map<string, number>(cohort_list.map((c, i) => [c, i])),
    [cohort_list],
  );

  const offsets = Array.from({ length: max_offsets }, (_, i) => i);
  const offsetPrefix = periodType === 'quarterly' ? 'Q' : 'M';
  const COL_COHORT = 72;
  const COL_ACQ = 48;
  const COL_CAC = 72;
  const COL_WIN = 40;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg">
      <div
        className="flex items-center gap-2 px-3 md:px-5 py-3 cursor-pointer hover:bg-[#192230] border-b border-gray-700"
        onClick={() => setCollapsed(!collapsed)}
      >
        <span className="text-gray-500 text-xs select-none w-3">{collapsed ? '▶' : '▼'}</span>
        <span className="font-bold text-white text-sm">Cumulative Gross Profit per Customer / CAC</span>
      </div>
      {!collapsed && (
        <div className="overflow-auto max-h-[60vh]">
          <table className="w-full text-[11px] md:text-xs">
            <thead className="sticky top-0 z-20">
              <tr className="border-b border-gray-700 bg-gray-900">
                <th className="sticky left-0 z-30 bg-gray-900 text-left px-3 py-2 text-gray-400 font-medium"
                    style={{ width: COL_COHORT, minWidth: COL_COHORT }}>Cohort</th>
                <th className="sticky z-30 bg-gray-900 text-right px-2 py-2 text-gray-400 font-medium"
                    style={{ left: COL_COHORT, width: COL_ACQ, minWidth: COL_ACQ }}># Acq</th>
                <th className="sticky z-30 bg-gray-900 text-right px-2 py-2 text-gray-400 font-medium"
                    style={{ left: COL_COHORT + COL_ACQ, width: COL_CAC, minWidth: COL_CAC }}>CAC</th>
                <th className="sticky z-30 bg-gray-900 text-center px-1 py-2 text-gray-500 font-medium"
                    style={{ left: COL_COHORT + COL_ACQ + COL_CAC, width: COL_WIN, minWidth: COL_WIN }}>Win</th>
                {offsets.map(i => (
                  <th key={i} className="bg-gray-900 text-right px-3 py-2 font-bold text-gray-200 whitespace-nowrap">
                    {offsetPrefix}{i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {axis.map(cohort => {
                const idx = observedPos.get(cohort);
                const rowVals = idx != null ? gm_over_cac[idx] : undefined;
                return (
                  <tr key={cohort} className="border-b border-gray-800/40 hover:bg-gray-800/30">
                    <td className="sticky left-0 z-10 bg-gray-900 px-3 py-1.5 text-gray-400 whitespace-nowrap"
                        style={{ width: COL_COHORT, minWidth: COL_COHORT }}>
                      {formatPeriod(cohort)}
                    </td>
                    <td className="sticky z-10 bg-gray-900 text-right px-2 py-1.5 text-gray-500"
                        style={{ left: COL_COHORT }}>
                      {idx != null ? acquired[cohort] : null}
                    </td>
                    <td className="sticky z-10 bg-gray-900 text-right px-2 py-1.5 text-gray-400"
                        style={{ left: COL_COHORT + COL_ACQ }}>
                      {idx != null ? (adaptive_cac[cohort] != null ? formatVal(adaptive_cac[cohort]!) : '—') : null}
                    </td>
                    <td className="sticky z-10 bg-gray-900 text-center px-1 py-1.5 text-gray-600"
                        style={{ left: COL_COHORT + COL_ACQ + COL_CAC }}>
                      {idx != null ? cac_window[cohort] : null}
                    </td>
                    {offsets.map(i => {
                      const val = rowVals?.[i] ?? null;
                      const isPaidBack = val != null && val >= 1.0;
                      return (
                        <td key={i} className={`text-right px-3 py-1.5 whitespace-nowrap ${
                          isPaidBack ? 'text-emerald-400 font-medium' : 'text-gray-300'
                        }`}>
                          {val != null
                            ? <KpiTooltip kpiKey="payback_cell" projectId={projectId}>{formatRatio(val)}</KpiTooltip>
                            : formatRatio(val)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Payback Chart ────────────────────────────────────────────────────────────

function PaybackChart({ payback, periodType }: { payback: PaybackData; periodType: string }) {
  const { cohort_list, gm_over_cac, max_offsets } = payback;
  const colors = useMemo(() => cohortColors(cohort_list.length), [cohort_list.length]);
  const offsetPrefix = periodType === 'quarterly' ? 'Q' : 'M';

  const chartData = useMemo(() => {
    const data: Record<string, any>[] = [];
    for (let offset = 0; offset < max_offsets; offset++) {
      const point: Record<string, any> = { name: `${offsetPrefix}${offset + 1}` };
      cohort_list.forEach((cohort, idx) => {
        const v = gm_over_cac[idx]?.[offset];
        if (v != null) point[cohort] = v;
      });
      data.push(point);
    }
    return data;
  }, [cohort_list, gm_over_cac, max_offsets]);

  // Dynamic y-axis: use 90th percentile + headroom, minimum 2.0x
  const yMax = useMemo(() => {
    const allVals: number[] = [];
    for (const row of gm_over_cac) {
      for (const v of row) {
        if (v != null && v > 0) allVals.push(v);
      }
    }
    if (allVals.length === 0) return 2;
    allVals.sort((a, b) => a - b);
    const p90 = allVals[Math.floor(allVals.length * 0.9)];
    const cap = Math.max(2, Math.ceil(p90 * 1.2 * 2) / 2); // round up to nearest 0.5x
    return cap;
  }, [gm_over_cac]);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-4">Cumulative Gross Profit per Customer / CAC</h3>
      <ResponsiveContainer width="100%" height={450}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis tickFormatter={(v: number) => `${v.toFixed(1)}x`} tick={{ fill: '#6b7280', fontSize: 10 }} domain={[0, yMax]} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            formatter={(value: any, name: any) => [`${Number(value).toFixed(1)}x`, formatPeriod(String(name))]}
          />
          <Legend
            wrapperStyle={{ fontSize: 10 }}
            formatter={(value: any) => formatPeriod(String(value))}
          />
          <ReferenceLine y={1} stroke="#22c55e" strokeDasharray="6 3" strokeWidth={2} label={{ value: '1.0x Payback', fill: '#34d399', fontSize: 10, position: 'right' }} />
          {cohort_list.map((cohort, idx) => (
            <Line
              key={cohort}
              type="monotone"
              dataKey={cohort}
              stroke={colors[idx]}
              strokeWidth={1.5}
              dot={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── LTV Callout Cards ────────────────────────────────────────────────────────

function LtvCallout({
  cacData,
  custData,
  projectId,
}: {
  cacData: CacResponse;
  custData: any;
  projectId: string;
}) {
  // Derive ACV per Customer: Ending RRR / Ending customer count (last period)
  const acv = useMemo(() => {
    if (!custData) return null;
    const periods: string[] = custData.period_cols || [];
    if (periods.length === 0) return null;
    const n = custData.period_type === 'quarterly' ? 4 : 12;
    const lastP = periods[periods.length - 1];
    const ending = custData.mrr_build?.[lastP]?.ending ?? 0;
    const count = custData.customer_build?.[lastP]?.ending ?? 0;
    if (count === 0) return null;
    return (ending * n) / count;
  }, [custData]);

  // Current GRR: last non-null value from GRR dict
  const grr = useMemo(() => {
    if (!custData?.grr) return null;
    const periods: string[] = custData.period_cols || [];
    for (let i = periods.length - 1; i >= 0; i--) {
      const v = custData.grr[periods[i]];
      if (v != null) return v;
    }
    return null;
  }, [custData]);

  const gmPct = cacData.current_gm_pct;
  const currentCac = cacData.current_quarterly_cac;

  // Formulaic LTV = ACV × GM% ÷ (1 − GRR/100)
  const churnRate = grr != null ? (1 - grr / 100) : null;
  const ltv = (acv != null && gmPct != null && churnRate != null && churnRate > 0)
    ? (acv * (gmPct / 100)) / churnRate
    : null;

  const ltvOverCac = (ltv != null && currentCac != null && currentCac > 0)
    ? ltv / currentCac
    : null;

  const impliedLifespan = (churnRate != null && churnRate > 0)
    ? 1 / churnRate
    : null;

  // Format helper for $ values
  const fmtDollar = (v: number) => {
    const k = v / _scale;
    return k < 1 ? `$${k.toFixed(1)}` : `$${Math.round(k).toLocaleString()}`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-gray-800/50 border border-gray-800 rounded-lg px-3 md:px-5 py-4">
        <p className="text-xs text-gray-500 mb-1">LTV (GRR Basis)</p>
        <p className="text-lg font-bold text-white">
          <KpiTooltip kpiKey="ltv_grr_basis" projectId={projectId}>{ltv != null ? fmtDollar(ltv) : '—'}</KpiTooltip>
        </p>
        <p className="text-[10px] text-gray-600 mt-2">
          ACV {acv != null ? fmtDollar(acv) : '—'} × GM {gmPct != null ? `${gmPct}%` : '—'} ÷ (1 − GRR {grr != null ? `${grr.toFixed(1)}%` : '—'})
        </p>
      </div>
      <div className="bg-gray-800/50 border border-gray-800 rounded-lg px-3 md:px-5 py-4">
        <p className="text-xs text-gray-500 mb-1">LTV / CAC</p>
        <p className={`text-lg font-bold ${ltvOverCac != null && ltvOverCac >= 3 ? 'text-emerald-400' : 'text-white'}`}>
          <KpiTooltip kpiKey="ltv_over_cac" projectId={projectId}>{ltvOverCac != null ? `${ltvOverCac.toFixed(1)}x` : '—'}</KpiTooltip>
        </p>
        <p className="text-[10px] text-gray-600 mt-2">
          {ltv != null ? fmtDollar(ltv) : '—'} / {currentCac != null ? fmtDollar(currentCac) : '—'}
        </p>
      </div>
      <div className="bg-gray-800/50 border border-gray-800 rounded-lg px-3 md:px-5 py-4">
        <p className="text-xs text-gray-500 mb-1">Implied Customer Lifespan</p>
        <p className="text-lg font-bold text-white">
          <KpiTooltip kpiKey="implied_customer_lifespan" projectId={projectId}>{impliedLifespan != null ? `${impliedLifespan.toFixed(1)} years` : '—'}</KpiTooltip>
        </p>
        <p className="text-[10px] text-gray-600 mt-2">
          1 ÷ (1 − GRR {grr != null ? `${grr.toFixed(1)}%` : '—'})
        </p>
      </div>
    </div>
  );
}

// ── Observed LTV Chart ──────────────────────────────────────────────────────

function ObservedLtvChart({ payback }: { payback: PaybackData }) {
  const { cohort_list, adaptive_cac, gm_over_cac } = payback;

  // For each cohort, last non-null gm_over_cac × adaptive_cac = cumulative GM per customer ($)
  const chartData = useMemo(() => {
    return cohort_list.map((cohort, idx) => {
      const row = gm_over_cac[idx] || [];
      const cac = adaptive_cac[cohort];
      // Find last non-null value in row
      let lastVal: number | null = null;
      for (let i = row.length - 1; i >= 0; i--) {
        if (row[i] != null) { lastVal = row[i]; break; }
      }
      const observed = (lastVal != null && cac != null) ? lastVal * cac : null;
      return {
        name: formatPeriod(cohort),
        value: observed != null ? observed / _scale : null,
      };
    }).filter(d => d.value != null);
  }, [cohort_list, adaptive_cac, gm_over_cac, _scale]);

  if (chartData.length === 0) return null;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-sm font-bold text-white mb-1">Cohort Observed LTV</h3>
      <p className="text-[10px] text-gray-600 mb-3">Cumulative gross profit per customer to date, by cohort vintage</p>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 9 }} interval="preserveStartEnd" tickLine={false} />
          <YAxis tickFormatter={(v: number) => `$${Math.round(v).toLocaleString()}`} tick={{ fill: '#6b7280', fontSize: 9 }} />
          <Tooltip
            contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: 8, fontSize: 11 }}
            formatter={(value: any) => [`$${Math.round(Number(value)).toLocaleString()}`, 'Observed LTV']}
          />
          <Bar dataKey="value" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Methodology Note ─────────────────────────────────────────────────────────

function MethodologyNote() {
  return (
    <div className="bg-gray-800/30 border border-gray-800 rounded-lg px-3 md:px-5 py-4 text-xs text-gray-500 leading-relaxed">
      <p className="font-medium text-gray-400 mb-1">CAC Attribution Methodology</p>
      <p>
        In the payback analysis above, CAC for each cohort is calculated using an adaptive trailing window.
        The algorithm starts with a quarterly (3-month) window of S&M spend divided by new customers acquired.
        If fewer than 3 customers were acquired in that window, it automatically expands to 6 months, then 9 months,
        then a full trailing 12 months (TTM). This adapts to the company's acquisition velocity (e.g., high-volume
        SaaS businesses with frequent customer additions use a quarterly window for precision, while enterprise
        businesses with infrequent acquisitions use a longer window to smooth out lumpiness).
        The "Win" column in the payback matrix indicates which window was applied to each cohort (Q = quarterly,
        6M = 6 months, 9M = 9 months, TTM = trailing 12 months).
      </p>
    </div>
  );
}

// ── Main View ────────────────────────────────────────────────────────────────

export default function CacPaybackView({ projectId, unitScale }: { projectId: string; unitScale: UnitScale }) {
  _scale = unitScale;
  const [data, setData]       = useState<CacResponse | null>(null);
  const [custData, setCustData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      authedFetch(apiUrl('/api/cac', projectId))
        .then(res => {
          if (!res.ok) throw new Error(res.status === 404 ? 'missing_data' : 'Fetch failed');
          return res.json();
        }),
      authedFetch(apiUrl('/api/customers/analytics', projectId))
        .then(res => res.ok ? res.json() : null)
        .catch(() => null),
    ]).then(([cac, cust]) => {
      setData(cac);
      setCustData(cust);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, [projectId]);

  if (loading) return <p className="text-gray-500 text-sm">Loading...</p>;
  if (error) return (
    <div className="text-center py-16">
      <p className="text-gray-500 text-sm">
        {error === 'missing_data'
          ? 'CAC analysis requires both financials and customer revenue data. Please upload both datasets.'
          : error}
      </p>
    </div>
  );
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <span className="text-gray-500 text-xs font-medium">{unitLabel(_scale as UnitScale)}</span>
      </div>

      {/* ── CAC Section ── */}
      <h3 className="text-sm font-bold text-white">CAC</h3>

      <CacTable data={data} projectId={projectId} />

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <CacLineChart data={data} />
        </div>
        <div className="w-64 flex-shrink-0">
          <CacCallout data={data} projectId={projectId} />
        </div>
      </div>

      <CacScatterChart data={data} />

      {/* ── Payback Section ── */}
      {data.payback.cohort_list.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-white pt-2">Payback</h3>
          <PaybackMatrix payback={data.payback} projectId={projectId} periodType={data.period_type} />
          <PaybackChart payback={data.payback} periodType={data.period_type} />
        </>
      )}

      {/* ── LTV Section ── */}
      <h3 className="text-sm font-bold text-white pt-2">LTV</h3>
      <LtvCallout cacData={data} custData={custData} projectId={projectId} />
      {data.payback.cohort_list.length > 0 && (
        <ObservedLtvChart payback={data.payback} />
      )}

      {/* ── Methodology ── */}
      <MethodologyNote />
    </div>
  );
}
